import React from "react";
import { useNavigate } from "react-router-dom";
import { Logo, Wordmark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ProgereLegalFooter } from "@/components/ProgereLegalFooter";

import {
  Flame, Sparkles, KanbanSquare, Bell, Plug, ShieldCheck, ArrowRight, Zap, Check, Crown,
} from "lucide-react";

const FEATURES = [
  { icon: Flame, title: "Score intelligent des prospects", text: "Priorisez automatiquement vos prospects chauds selon le secteur, la valeur et l'urgence." },
  { icon: Sparkles, title: "Assistant IA de vente", text: "Résumés de dossiers, prochaines actions et relances rédigées par l'IA (Claude)." },
  { icon: KanbanSquare, title: "Pipeline & soumissions", text: "Kanban glisser-déposer, soumissions PDF et conversion en projet." },
  { icon: Bell, title: "Relances automatiques", text: "Relances après soumission, clients inactifs et rappels avant expiration." },
  { icon: Plug, title: "Écosystème PROGÈRE", text: "Connecté à PUNCH, ERP, COMPTA, PAIE et DEVIS AI pour un cycle client complet." },
  { icon: ShieldCheck, title: "Sécurité & Loi 25", text: "Chiffrement, rôles, consentements et conformité québécoise." },
];

const PLANS = [
  { name: "PROGÈRE CRM Starter", price: "69,00 $", period: "CAD/mois", seats: ["3 utilisateurs", "Jusqu'à 250 contacts"], features: ["1 pipeline de vente", "Gestion clients et prospects", "Tâches et suivis de base", "Historique des communications", "Export CSV"] },
  { name: "PROGÈRE CRM Pro", price: "149,00 $", period: "CAD/mois", featured: true, seats: ["10 utilisateurs", "Jusqu'à 2 500 contacts"], features: ["Pipelines illimités", "Automatisations de relance", "Devis reliés aux clients", "Tableau de bord des ventes", "Import/export Excel, CSV et PDF", "CRM AI : 100 analyses/mois"] },
  { name: "PROGÈRE CRM Entreprise", price: "299,00 $", period: "CAD/mois", seats: ["Utilisateurs illimités", "Contacts illimités"], features: ["Tout Pro", "Automatisations avancées", "Segmentation client avancée", "Intégration PUNCH, ERP et COMPTA", "CRM AI illimité", "Audit log complet", "Gestion multi-compagnies"] },
];

export default function Landing() {
  const navigate = useNavigate();
  const scrollToForfaits = () => document.getElementById("forfaits")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#07080A] text-white pg-scroll">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#07080A]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Logo className="h-12" />
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <button onClick={() => navigate("/login")} data-testid="header-login-btn" className="btn-gold rounded-full px-5 py-2 text-sm flex items-center gap-2">
              Se connecter <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pg-grid-bg">
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
          <h1 className="pg-fade-up">
            <Wordmark className="text-5xl sm:text-6xl lg:text-7xl" />
          </h1>
          <p className="mt-6 text-lg sm:text-xl font-semibold text-white">CRM intelligent pour la construction au Québec.</p>
          <p className="mt-3 text-gray-400 max-w-2xl mx-auto">
            Prospects, pipeline, soumissions, relances automatiques et assistant IA — un module natif de l'écosystème PROGÈRE ERP.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/login")} data-testid="cta-start-btn" className="btn-gold rounded-xl px-7 py-3.5 text-sm font-bold">Commencer maintenant</button>
            <button onClick={scrollToForfaits} data-testid="cta-forfaits-btn" className="rounded-xl border border-[#F2BC2E]/40 text-[#F2BC2E] px-7 py-3.5 text-sm font-bold hover:bg-[#F2BC2E]/10 transition-colors">Voir les forfaits</button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-[#111] border border-white/5 rounded-2xl p-6 hover:bg-[#161616] transition-colors">
              <f.icon size={22} className="text-[#F2BC2E]" strokeWidth={1.6} />
              <h3 className="mt-4 font-semibold text-white">{f.title}</h3>
              <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Forfaits */}
      <section id="forfaits" className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white">Des forfaits pour chaque entreprise</h2>
          <p className="text-gray-500 mt-2">Sans engagement. Évoluez à mesure que votre équipe grandit.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {PLANS.map((p) => {
            const eco = p.ecosystem;
            return (
              <div key={p.name} className={`relative rounded-2xl p-6 flex flex-col border ${eco ? "border-[#F2BC2E]/40 bg-gradient-to-b from-[#1a1608] to-[#0f0f0f]" : "border-white/5 bg-[#141414]"}`}>
                <div className="flex items-center gap-2 text-[#F2BC2E] mb-4">{eco ? <Crown size={18} /> : <Zap size={16} />}<span className="text-sm font-semibold text-white">{p.name}</span></div>
                <div className="mb-4"><span className="font-display text-3xl font-bold text-white">{p.price}</span>{p.period && <span className="text-xs text-gray-500 ml-1">{p.period}</span>}</div>
                <div className="rounded-xl border border-white/10 bg-[#0f0f0f] p-3 mb-4 space-y-1.5">
                  {p.seats.map((s) => <p key={s} className="text-xs text-gray-300 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#F2BC2E]" /> {s}</p>)}
                </div>
                {eco && p.suite && (
                  <div className="rounded-xl border border-[#F2BC2E]/30 p-3 mb-4">
                    <p className="text-[10px] font-bold tracking-widest uppercase text-[#F2BC2E] mb-2">Suite complète incluse</p>
                    <div className="flex flex-wrap gap-1.5">{p.suite.map((s) => <span key={s} className="text-[10px] font-bold text-[#F2BC2E] border border-[#F2BC2E]/40 rounded-md px-2 py-1">{s}</span>)}</div>
                  </div>
                )}
                <ul className="space-y-2 flex-1 mb-6">{p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-gray-300"><Check size={14} className="text-emerald-400 mt-0.5 shrink-0" /> {f}</li>)}</ul>
                <button onClick={() => navigate("/login")} className={`w-full rounded-full py-2.5 text-sm font-bold transition-colors ${eco ? "btn-gold" : "border border-white/15 text-white hover:bg-white/5"}`}>{eco ? "Demander une soumission" : "Choisir ce forfait"}</button>
              </div>
            );
          })}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6"><ProgereLegalFooter /></div>
    </div>
  );
}
